import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import axios from 'axios';
import { ArrowLeft, FileText, Eye, X, Download, Printer } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || '/api';

/**
 * Admin submissions browser. Lists every submission for a single form;
 * clicking a row opens a detail modal with the full per-question answer
 * breakdown. Multi-select / checkbox answers are JSON-encoded server-side
 * and re-parsed here for display.
 *
 * Routed at /forms/:id/submissions.
 */
export default function FormSubmissions() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openSid, setOpenSid] = useState(null);

  const authHeader = useCallback(() => ({
    headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` },
  }), []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [formRes, subsRes] = await Promise.all([
        axios.get(`${API_URL}/forms/${id}`, authHeader()),
        axios.get(`${API_URL}/forms/${id}/submissions`, authHeader()),
      ]);
      setForm(formRes.data);
      setSubmissions(subsRes.data || []);
    } catch (e) {
      toast.error('Failed to load submissions');
      navigate('/forms');
    } finally {
      setLoading(false);
    }
  }, [id, authHeader, navigate]);

  useEffect(() => { load(); }, [load]);

  /**
   * Auth-gated CSV download. The endpoint requires a Bearer token, so we
   * fetch as a Blob, wrap in an object URL, and trigger an anchor click
   * — a plain <a href> would not include the auth header.
   */
  const exportCsv = async () => {
    try {
      const res = await axios.get(`${API_URL}/forms/${id}/submissions.csv`, {
        headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` },
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8' });
      const url = window.URL.createObjectURL(blob);
      const safeName = (form?.name || 'form').replace(/[^A-Za-z0-9_-]+/g, '_');
      const today = new Date().toISOString().slice(0, 10);
      const a = document.createElement('a');
      a.href = url;
      a.download = `submissions-${safeName}-${today}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Failed to export CSV');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
      </div>
    );
  }
  if (!form) return null;

  return (
    <div className="min-h-screen bg-gray-50 py-6">
      <div className="max-w-5xl mx-auto px-4">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/forms')}
              className="p-2 hover:bg-gray-200 rounded-lg"
              title="Back to forms"
            >
              <ArrowLeft size={24} className="text-gray-600" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center space-x-2">
                <FileText className="text-primary-600" size={26} />
                <span>{form.name} — Submissions</span>
              </h1>
              <p className="text-sm text-gray-600">
                {submissions.length} submission{submissions.length === 1 ? '' : 's'}
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={exportCsv}
              disabled={submissions.length === 0}
              className="btn btn-secondary flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
              title={submissions.length === 0 ? 'No submissions to export' : 'Download all submissions as CSV'}
            >
              <Download size={16} />
              <span>Export CSV</span>
            </button>
            <button onClick={() => navigate(`/forms/${id}`)} className="btn btn-secondary">
              Edit form
            </button>
          </div>
        </div>

        {submissions.length === 0 ? (
          <div className="card text-center py-12 text-gray-500">
            <FileText size={48} className="mx-auto mb-3 text-gray-300" />
            <p className="font-medium">No submissions yet.</p>
            <p className="text-sm mt-1">Submissions appear here once a client fills the form.</p>
          </div>
        ) : (
          <div className="card overflow-hidden p-0">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left text-xs font-semibold text-gray-700 px-4 py-3">Submitted</th>
                  <th className="text-left text-xs font-semibold text-gray-700 px-4 py-3">Submitter</th>
                  <th className="text-left text-xs font-semibold text-gray-700 px-4 py-3">Email</th>
                  <th className="text-left text-xs font-semibold text-gray-700 px-4 py-3">Linked client</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {submissions.map((s) => (
                  <tr key={s.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-800">
                      {s.submitted_at ? new Date(s.submitted_at).toLocaleString() : '—'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-800">
                      {s.submitter_name || (s.client_first_name ? `${s.client_first_name} ${s.client_last_name || ''}`.trim() : <span className="text-gray-400">Anonymous</span>)}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {s.submitter_email || s.client_email || <span className="text-gray-400">—</span>}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {s.client_id
                        ? <span className="inline-block px-2 py-0.5 rounded text-xs bg-green-100 text-green-700">Logged-in client</span>
                        : <span className="inline-block px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-600">Token submission</span>}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => setOpenSid(s.id)}
                        className="p-2 text-primary-600 hover:bg-primary-50 rounded"
                        title="View answers"
                      >
                        <Eye size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {openSid && (
        <SubmissionDetailModal
          sid={openSid}
          onClose={() => setOpenSid(null)}
        />
      )}
    </div>
  );
}

function SubmissionDetailModal({ sid, onClose }) {
  const [submission, setSubmission] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = sessionStorage.getItem('token');
        const res = await axios.get(`${API_URL}/forms/submissions/${sid}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) setSubmission(res.data);
      } catch (e) {
        toast.error('Failed to load submission');
        onClose();
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [sid, onClose]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-5 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white">
          <h2 className="text-xl font-bold flex items-center space-x-2">
            <FileText className="text-primary-600" size={22} />
            <span>Submission detail</span>
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={20} />
          </button>
        </div>

        {loading ? (
          <div className="p-12 flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
          </div>
        ) : submission && (
          <div className="p-5 space-y-4">
            <div className="bg-gray-50 border border-gray-200 rounded p-3 text-sm space-y-1">
              <div><strong>Submitted:</strong> {submission.submitted_at ? new Date(submission.submitted_at).toLocaleString() : '—'}</div>
              {submission.submitter_name && <div><strong>Name:</strong> {submission.submitter_name}</div>}
              {submission.submitter_email && <div><strong>Email:</strong> {submission.submitter_email}</div>}
              {submission.client_first_name && (
                <div>
                  <strong>Linked client:</strong> {submission.client_first_name} {submission.client_last_name}
                  {submission.client_email && <span className="text-gray-500"> ({submission.client_email})</span>}
                </div>
              )}
            </div>

            {submission.answers.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-6">No answers recorded.</p>
            ) : (
              <div className="space-y-3">
                {submission.answers.map((a) => (
                  <AnswerRow key={a.question_id} answer={a} />
                ))}
              </div>
            )}

            {submission.signature_data_url && (
              <div>
                <h3 className="text-sm font-semibold text-gray-800 mb-2">Signature</h3>
                <img
                  src={submission.signature_data_url}
                  alt="Signature"
                  className="border border-gray-300 rounded bg-white max-w-full"
                />
              </div>
            )}
          </div>
        )}

        <div className="p-5 border-t border-gray-200 bg-gray-50 flex justify-end space-x-2">
          <button
            onClick={() => printSubmission(submission)}
            disabled={!submission}
            className="btn btn-secondary flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
            title="Open a print-ready view (use your browser's Save as PDF for a PDF copy)"
          >
            <Printer size={16} />
            <span>Print</span>
          </button>
          <button onClick={onClose} className="btn btn-secondary">Close</button>
        </div>
      </div>
    </div>
  );
}

function AnswerRow({ answer }) {
  const raw = answer.value_text || '';

  // File-upload answers are JSON envelopes with a `url` key. Detect before
  // the array branch since these also start with `{`.
  if (typeof raw === 'string' && raw.trim().startsWith('{')) {
    try {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed.url === 'string') {
        const isImage = typeof parsed.mime === 'string' && parsed.mime.startsWith('image/');
        const sizeKb = parsed.size ? ` · ${parsed.size < 1024 * 1024
          ? `${(parsed.size / 1024).toFixed(1)} KB`
          : `${(parsed.size / (1024 * 1024)).toFixed(1)} MB`}` : '';
        return (
          <div>
            <div className="text-xs font-semibold text-gray-700 mb-1">{answer.label}</div>
            <div className="flex items-start space-x-3 p-2 bg-gray-50 border border-gray-200 rounded">
              {isImage && (
                <img
                  src={parsed.url}
                  alt={parsed.filename || 'uploaded file'}
                  className="w-16 h-16 object-cover rounded border border-gray-200"
                />
              )}
              <div className="flex-1 min-w-0">
                <a
                  href={parsed.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm font-medium text-primary-600 hover:underline truncate block"
                >
                  {parsed.filename || 'Download file'}
                </a>
                <div className="text-xs text-gray-500">
                  {parsed.mime || 'unknown type'}{sizeKb}
                </div>
              </div>
            </div>
          </div>
        );
      }
    } catch (_) { /* fall through */ }
  }

  let display = raw;
  // Multi-select / checkbox answers come back JSON-encoded; render as a list.
  if (typeof display === 'string' && display.trim().startsWith('[')) {
    try {
      const arr = JSON.parse(display);
      if (Array.isArray(arr)) {
        display = arr.length === 0 ? <span className="text-gray-400">(none selected)</span> : (
          <ul className="list-disc list-inside text-sm text-gray-800">
            {arr.map((v, i) => <li key={i}>{v}</li>)}
          </ul>
        );
      }
    } catch (_) { /* fall through to plain text */ }
  }

  // Signature answers (data URL) render as an image.
  if (typeof display === 'string' && display.startsWith('data:image/')) {
    return (
      <div>
        <div className="text-xs font-semibold text-gray-700 mb-1">{answer.label}</div>
        <img src={display} alt={answer.label} className="border border-gray-300 rounded bg-white max-w-full" />
      </div>
    );
  }

  return (
    <div>
      <div className="text-xs font-semibold text-gray-700 mb-1">{answer.label}</div>
      <div className="text-sm text-gray-800 whitespace-pre-wrap">
        {display === '' ? <span className="text-gray-400">(blank)</span> : display}
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Print-friendly submission rendering.
//
// We open a brand-new window and stream a self-contained HTML document
// into it. This sidesteps the app's global Tailwind / toast / modal
// stacking and gives the browser's print dialog a clean canvas. Every
// modern browser exposes "Save as PDF" as a destination in that same
// dialog, so we don't ship a PDF library.
//
// What we render:
//   - business / form title header
//   - submitted-at + submitter + linked-client metadata
//   - one block per question/answer (including file-upload links and
//     image previews for image uploads + signature data URLs)
//   - the captured signature pad image if `signature_data_url` is set
//
// HTML-escapes every string we interpolate to keep raw user input from
// breaking the markup.
// ──────────────────────────────────────────────────────────────────────

function escapeHtml(s) {
  if (s == null) return '';
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function renderAnswerForPrint(answer) {
  const raw = answer.value_text || '';
  const labelHtml = `<div class="q-label">${escapeHtml(answer.label || '')}</div>`;

  // File-upload envelope: JSON with a `url` key.
  if (typeof raw === 'string' && raw.trim().startsWith('{')) {
    try {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed.url === 'string') {
        const isImage = typeof parsed.mime === 'string' && parsed.mime.startsWith('image/');
        const meta = `${escapeHtml(parsed.filename || 'uploaded file')} <span class="muted">(${escapeHtml(parsed.mime || 'unknown type')})</span>`;
        const img = isImage ? `<div class="file-img"><img src="${escapeHtml(parsed.url)}" alt="" /></div>` : '';
        return `<div class="qa">${labelHtml}<div class="q-answer">${meta}${img}</div></div>`;
      }
    } catch (_) { /* fall through */ }
  }

  // Multi-select / checkbox: JSON array.
  if (typeof raw === 'string' && raw.trim().startsWith('[')) {
    try {
      const arr = JSON.parse(raw);
      if (Array.isArray(arr)) {
        if (arr.length === 0) {
          return `<div class="qa">${labelHtml}<div class="q-answer muted">(none selected)</div></div>`;
        }
        const items = arr.map((v) => `<li>${escapeHtml(v)}</li>`).join('');
        return `<div class="qa">${labelHtml}<ul class="q-answer">${items}</ul></div>`;
      }
    } catch (_) { /* fall through */ }
  }

  // Inline signature data URL.
  if (typeof raw === 'string' && raw.startsWith('data:image/')) {
    return `<div class="qa">${labelHtml}<div class="q-answer"><img class="sig-img" src="${escapeHtml(raw)}" alt="" /></div></div>`;
  }

  const text = raw === ''
    ? '<span class="muted">(blank)</span>'
    : escapeHtml(raw);
  return `<div class="qa">${labelHtml}<div class="q-answer">${text}</div></div>`;
}

function printSubmission(submission) {
  if (!submission) return;
  const win = window.open('', '_blank', 'noopener,noreferrer,width=900,height=1100');
  if (!win) {
    toast.error('Could not open print window — check your pop-up blocker.');
    return;
  }

  const submitted = submission.submitted_at
    ? new Date(submission.submitted_at).toLocaleString()
    : '—';
  const formName = submission.form_name || submission.form?.name || 'Form submission';

  const metaRows = [];
  metaRows.push(`<div><span class="meta-k">Submitted:</span> ${escapeHtml(submitted)}</div>`);
  if (submission.submitter_name) metaRows.push(`<div><span class="meta-k">Name:</span> ${escapeHtml(submission.submitter_name)}</div>`);
  if (submission.submitter_email) metaRows.push(`<div><span class="meta-k">Email:</span> ${escapeHtml(submission.submitter_email)}</div>`);
  if (submission.client_first_name) {
    const linked = `${submission.client_first_name} ${submission.client_last_name || ''}`.trim();
    const email = submission.client_email ? ` (${submission.client_email})` : '';
    metaRows.push(`<div><span class="meta-k">Linked client:</span> ${escapeHtml(linked)}${escapeHtml(email)}</div>`);
  }

  const answersHtml = (submission.answers || []).map(renderAnswerForPrint).join('') ||
    '<p class="muted center">No answers recorded.</p>';

  const signatureHtml = submission.signature_data_url
    ? `<div class="sig-block"><h3>Signature</h3><img class="sig-img" src="${escapeHtml(submission.signature_data_url)}" alt="" /></div>`
    : '';

  const html = `<!doctype html>
<html><head><meta charset="utf-8" />
<title>${escapeHtml(formName)} — submission</title>
<style>
  * { box-sizing: border-box; }
  body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
         color: #111; margin: 32px; line-height: 1.45; }
  h1 { font-size: 22px; margin: 0 0 4px; }
  h2 { font-size: 14px; font-weight: 600; color: #555; margin: 0 0 24px; text-transform: uppercase; letter-spacing: 0.05em; }
  h3 { font-size: 14px; margin: 24px 0 8px; }
  .meta { background: #f5f5f5; padding: 12px 14px; border-radius: 6px; font-size: 13px; margin-bottom: 24px; }
  .meta div { margin: 2px 0; }
  .meta-k { font-weight: 600; color: #333; }
  .qa { margin-bottom: 16px; page-break-inside: avoid; }
  .q-label { font-size: 12px; font-weight: 600; color: #444; margin-bottom: 4px; text-transform: uppercase; letter-spacing: 0.04em; }
  .q-answer { font-size: 14px; color: #111; white-space: pre-wrap; padding: 6px 10px; border-left: 3px solid #ddd; background: #fafafa; }
  .q-answer ul, ul.q-answer { margin: 0; padding-left: 18px; }
  .q-answer li { margin: 2px 0; }
  .muted { color: #888; }
  .center { text-align: center; }
  .file-img { margin-top: 8px; }
  .file-img img { max-width: 100%; max-height: 240px; border: 1px solid #ddd; border-radius: 4px; }
  .sig-img { max-width: 100%; max-height: 200px; border: 1px solid #ddd; border-radius: 4px; background: white; }
  .sig-block { margin-top: 32px; page-break-inside: avoid; }
  @media print {
    body { margin: 0.5in; }
    .no-print { display: none; }
  }
</style>
</head><body>
  <h1>${escapeHtml(formName)}</h1>
  <h2>Form submission</h2>
  <div class="meta">${metaRows.join('')}</div>
  ${answersHtml}
  ${signatureHtml}
  <script>
    // Wait for images to settle before triggering print so they're in
    // the canvas. setTimeout fallback covers cached/instant loads where
    // 'load' may already have fired.
    window.addEventListener('load', function () { setTimeout(function () { window.focus(); window.print(); }, 150); });
    setTimeout(function () { try { window.focus(); window.print(); } catch (e) {} }, 800);
  <\/script>
</body></html>`;

  win.document.open();
  win.document.write(html);
  win.document.close();
}
